import { batchActions, countryFacetedCountsToCountryDemographics } from './utils';
import { setData } from './data';
import { setStatus, unsetStatus } from './status';
import { LOADING, ERROR, selectNetworkDemographicsStatus, selectDateRange } from './selectors';
import {
  calculateRoundedPercentage,
  distributeObjectFractals,
  calculateRawPercentage,
} from '../lib/Formatter';
import { toJSON, PODCASTERS_SERVICE } from '../lib/Request';
import {
  DateRangeWithLabel,
  Thunk,
  AgeFacetedCounts,
  CountryFactedCounts,
  GenderedCounts,
  GenderDemographics,
  GenderDemographicsPercentagePerRange,
  GenderDemographicsPercentagePerGender,
  GenderWithPercentage,
  NetworkAudienceDemographics,
} from '../types';

type NetworkAggregateResponse = {
  count: number;
  start: string;
  end: string;
  ageFacetedCounts: AgeFacetedCounts;
  countryFacetedCounts?: CountryFactedCounts;
  genderedCounts: GenderedCounts;
};

export const loadNetworkDemographics = (networkId: string): Thunk => async (
  dispatch,
  getState,
  { request },
) => {
  const key = 'networkDemographics';
  const { startDate, endDate } = selectDateRange(getState()) as DateRangeWithLabel;
  const endpoint = `${PODCASTERS_SERVICE}/v0/networks/${networkId}/aggregate?start=${startDate}&end=${endDate}`;

  /* istanbul ignore next */
  if (selectNetworkDemographicsStatus(getState()) === LOADING) return;

  dispatch(setStatus({ [key]: LOADING }, 'audience/LOAD_NETWORK_DEMOGRAPHICS_REQUEST'));

  try {
    const data: NetworkAggregateResponse = await request.get(endpoint).then(toJSON);

    const {
      ageFacetedCounts: dataByAgeRange,
      count: listenerTotal,
      genderedCounts: { counts: countByGender },
    } = data;

    const genderDemographicsByAgeGroup: GenderDemographics[] = Object.keys(dataByAgeRange)
      .sort()
      .map(ageRange => {
        const { MALE = 0, FEMALE = 0, NOT_SPECIFIED = 0, NON_BINARY = 0 } = dataByAgeRange[
          ageRange as keyof AgeFacetedCounts
        ].counts;
        const totalAgeRangeCount = MALE + FEMALE + NOT_SPECIFIED + NON_BINARY;
        const ageRangePercentage = calculateRawPercentage(totalAgeRangeCount, listenerTotal);

        const percentagePerRange = distributeObjectFractals(
          {
            malePercentage: calculateRawPercentage(MALE, totalAgeRangeCount),
            femalePercentage: calculateRawPercentage(FEMALE, totalAgeRangeCount),
            notSpecifiedPercentage: calculateRawPercentage(NOT_SPECIFIED, totalAgeRangeCount),
            nonBinaryPercentage: calculateRawPercentage(NON_BINARY, totalAgeRangeCount),
          },
          100,
        ) as GenderDemographicsPercentagePerRange;

        const percentagePerGender: GenderDemographicsPercentagePerGender = {
          male: calculateRawPercentage(MALE, listenerTotal),
          female: calculateRawPercentage(FEMALE, listenerTotal),
          notSpecified: calculateRawPercentage(NOT_SPECIFIED, listenerTotal),
          nonBinary: calculateRawPercentage(NON_BINARY, listenerTotal),
        };

        return {
          ageRange,
          ageRangePercentage,
          ...percentagePerGender,
          ...percentagePerRange,
        };
      });

    const percentageByGender: GenderWithPercentage[] = [
      {
        gender: 'female',
        percentage: calculateRoundedPercentage(countByGender.FEMALE, listenerTotal),
      },
      {
        gender: 'male',
        percentage: calculateRoundedPercentage(countByGender.MALE, listenerTotal),
      },
      {
        gender: 'notSpecified',
        percentage: calculateRoundedPercentage(countByGender.NOT_SPECIFIED, listenerTotal),
      },
      {
        gender: 'nonBinary',
        percentage: calculateRoundedPercentage(countByGender.NON_BINARY, listenerTotal),
      },
    ];

    const countryFacetedCounts = data.countryFacetedCounts || /* istanbul ignore next */ {};

    const countryDemographics = countryFacetedCountsToCountryDemographics(countryFacetedCounts);

    const normalized: NetworkAudienceDemographics = {
      ...data,
      genderDemographicsByAgeGroup,
      percentageByGender,
      countryDemographics,
      countryFacetedCounts,
    };

    dispatch(
      batchActions(
        [setData({ [key]: normalized }), unsetStatus([key])],
        'audience/LOAD_NETWORK_DEMOGRAPHICS_SUCCESS',
      ),
    );
  } catch (err) {
    switch (err.name) {
      case 'ResponseError':
      case 'TokenError': {
        dispatch(setStatus({ [key]: ERROR }, 'audience/LOAD_NETWORK_DEMOGRAPHICS_ERROR'));
        break;
      }

      /* istanbul ignore next */
      default:
        throw err;
    }
  }
};
